/**
 * SettingsPage - Page des paramètres
 * @class SettingsPage
 */
import { getIcon } from '../utils/icons.js';
import { t, getCurrentLang, setLang } from '../utils/i18n.js';

export class SettingsPage {
    /**
     * Crée une instance de SettingsPage
     * @param {Object} options - Options de la page
     */
    constructor(options = {}) {
        this._services = options.services || {};
        this._state = options.state || {};
        this._onSettingsChange = options.onSettingsChange;
        this._container = null;

        // Devises communes pour la sélection par défaut
        this._currencies = [
            { code: 'EUR', name: 'Euro' },
            { code: 'USD', name: 'Dollar américain' },
            { code: 'GBP', name: 'Livre sterling' },
            { code: 'JPY', name: 'Yen japonais' },
            { code: 'CHF', name: 'Franc suisse' },
            { code: 'CAD', name: 'Dollar canadien' },
            { code: 'AUD', name: 'Dollar australien' },
            { code: 'CNY', name: 'Yuan chinois' }
        ];
    }

    /**
     * Rend la page
     * @param {HTMLElement} container - Conteneur
     */
    async render(container) {
        this._container = container;

        const currentTheme = localStorage.getItem('geocurrency-theme') || 'light';
        const currentLang = getCurrentLang();
        const defaultCurrency = localStorage.getItem('geocurrency-default-currency') || 'EUR';

        container.innerHTML = `
            <div class="page settings-page">
                <div class="page-header">
                    <h1 class="page-title">
                        <span class="page-title-icon">${getIcon('settings')}</span>
                        ${t('settings_title')}
                    </h1>
                    <p class="page-subtitle">${t('settings_subtitle')}</p>
                </div>
                
                <div class="settings-section">
                    <h2 class="settings-section-title">
                        ${getIcon('globe')} ${t('section_language')}
                    </h2>
                    <div class="card settings-card">
                        <div class="setting-item">
                            <div class="setting-info">
                                <div class="setting-label">${t('language_label')}</div>
                                <div class="setting-description">${t('language_desc')}</div>
                            </div>
                            <div class="setting-control">
                                <div class="language-toggle">
                                    <button class="btn ${currentLang === 'fr' ? 'btn-primary' : 'btn-outline'}" data-lang="fr">FR</button>
                                    <button class="btn ${currentLang === 'en' ? 'btn-primary' : 'btn-outline'}" data-lang="en">EN</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="settings-section">
                    <h2 class="settings-section-title">
                        ${getIcon('sun')} ${t('section_appearance')}
                    </h2>
                    <div class="card settings-card">
                        <div class="setting-item">
                            <div class="setting-info">
                                <div class="setting-label">${t('theme_dark')}</div>
                                <div class="setting-description">${t('theme_dark_desc')}</div>
                            </div>
                            <div class="setting-control">
                                <label class="toggle-switch">
                                    <input type="checkbox" id="dark-theme-toggle" ${currentTheme === 'dark' ? 'checked' : ''}>
                                    <span class="toggle-slider"></span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="settings-section">
                    <h2 class="settings-section-title">
                        ${getIcon('dollarSign')} ${t('section_preferences')}
                    </h2>
                    <div class="card settings-card">
                        <div class="setting-item">
                            <div class="setting-info">
                                <div class="setting-label">${t('default_currency')}</div>
                                <div class="setting-description">${t('default_currency_desc')}</div>
                            </div>
                            <div class="setting-control">
                                <select id="default-currency" class="form-select" style="width: auto;">
                                    ${this._createCurrencyOptions(defaultCurrency)}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="settings-section">
                    <h2 class="settings-section-title">
                        ${getIcon('database')} ${t('section_data')}
                    </h2>
                    <div class="card settings-card">
                        <!-- History section removed -->
                        
                        <div class="setting-item">
                            <div class="setting-info">
                                <div class="setting-label">${getIcon('refresh')} ${t('cache_data')}</div>
                                <div class="setting-description">${t('cache_data_desc')}</div>
                            </div>
                            <div class="setting-control">
                                <button class="btn btn-secondary" id="clear-cache">
                                    ${getIcon('refresh')}
                                    ${t('refresh')}
                                </button>
                            </div>
                        </div>
                        
                        <div class="setting-item">
                            <div class="setting-info">
                                <div class="setting-label">${t('reset_app')}</div>
                                <div class="setting-description">${t('reset_app_desc')}</div>
                            </div>
                            <div class="setting-control">
                                <button class="btn btn-danger" id="reset-app">
                                    ${getIcon('alertTriangle')}
                                    ${t('reset_btn')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                
                <div class="settings-section">
                    <h2 class="settings-section-title">
                        ${getIcon('info')} À propos
                    </h2>
                    <div class="card settings-card" style="background: linear-gradient(135deg, var(--color-primary-bg), var(--bg-secondary)); border-left: 4px solid var(--color-primary);">
                        <div class="setting-item">
                            <div class="setting-info">
                                <div class="setting-label">${t('about_desc')}</div>
                                <div class="setting-description">${t('about_subdesc')}</div>
                            </div>
                        </div>
                        <div class="setting-item" style="border-top: 1px solid var(--border-color); padding-top: var(--spacing-md); margin-top: var(--spacing-md);">
                            <div class="setting-info">
                                <div class="setting-label">${t('about_apis')}</div>
                                <div class="setting-description">
                                    <p style="margin-bottom: var(--spacing-xs);">
                                        <a href="https://restcountries.com" target="_blank" rel="noopener" style="color: var(--color-primary); text-decoration: none;">RestCountries</a> - ${t('about_apis_desc')}
                                    </p>
                                    <p style="margin-bottom: var(--spacing-xs);">
                                        <a href="https://www.frankfurter.app" target="_blank" rel="noopener" style="color: var(--color-primary); text-decoration: none;">Frankfurter</a> - ${t('about_apis_rates')}
                                    </p>
                                    <p>
                                        <a href="https://www.openstreetmap.org" target="_blank" rel="noopener" style="color: var(--color-primary); text-decoration: none;">OpenStreetMap</a> - ${t('about_apis_map')}
                                    </p>
                                </div>
                            </div>
                        </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;

        this._setupEventListeners();
    }

    /**
     * Crée les options de devises
     * @param {string} selected - Devise sélectionnée
     * @returns {string} HTML
     * @private
     */
    _createCurrencyOptions(selected) {
        return this._currencies.map(c => `
            <option value="${c.code}" ${c.code === selected ? 'selected' : ''}>
                ${c.code} - ${c.name}
            </option>
        `).join('');
    }

    /**
     * Configure les écouteurs
     * @private
     */
    _setupEventListeners() {
        // Langue
        const languageBtns = this._container.querySelectorAll('[data-lang]');
        languageBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const lang = e.target.dataset.lang;
                setLang(lang);
                
                // On notifie l'application du changement de langue
                window.dispatchEvent(new CustomEvent('geocurrency:language-change', { detail: lang }));
                
                // Mettre à jour l'état actif des boutons immédiatement (visuel)
                languageBtns.forEach(b => {
                    if (b.dataset.lang === lang) {
                        b.classList.remove('btn-outline');
                        b.classList.add('btn-primary');
                    } else {
                        b.classList.add('btn-outline');
                        b.classList.remove('btn-primary');
                    }
                });
            });
        });

        // Thème sombre
        const darkThemeToggle = this._container.querySelector('#dark-theme-toggle');
        darkThemeToggle?.addEventListener('change', (e) => {
            const theme = e.target.checked ? 'dark' : 'light';
            this._setTheme(theme);
        });

        // Devise par défaut
        const currencySelect = this._container.querySelector('#default-currency');
        currencySelect?.addEventListener('change', (e) => {
            localStorage.setItem('geocurrency-default-currency', e.target.value);
            this._showNotification('Devise par défaut mise à jour', 'success');
        });

        // Vider le cache
        const clearCache = this._container.querySelector('#clear-cache');
        clearCache?.addEventListener('click', () => {
            if (this._onSettingsChange) {
                this._onSettingsChange({ clearCache: true });
            }
            this._showNotification('Cache vidé avec succès', 'success');
        });

        // Réinitialiser
        const resetApp = this._container.querySelector('#reset-app');
        resetApp?.addEventListener('click', () => {
            if (confirm('Êtes-vous sûr de vouloir réinitialiser l\'application ? Toutes vos données seront perdues.')) {
                localStorage.clear();
                location.reload();
            }
        });
    }

    /**
     * Définit le thème
     * @param {string} theme - Thème
     * @private
     */
    _setTheme(theme) {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('geocurrency-theme', theme);
    }

    /**
     * Affiche une notification
     * @param {string} message - Message
     * @param {string} type - Type (success, error, info, warning)
     * @private
     */
    _showNotification(message, type = 'info') {
        const container = document.getElementById('notification-container');
        if (!container) return;

        const iconMap = {
            success: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>',
            error: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>',
            info: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>',
            warning: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>'
        };

        const notification = document.createElement('div');
        notification.className = `notification notification-${type}`;
        notification.innerHTML = `
            <span class="notification-icon">${iconMap[type] || iconMap.info}</span>
            <span class="notification-message">${message}</span>
            <button class="notification-close" aria-label="Fermer">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
        `;

        notification.querySelector('.notification-close').addEventListener('click', () => {
            notification.remove();
        });

        container.appendChild(notification);

        setTimeout(() => {
            if (notification.parentNode) {
                notification.classList.add('fade-out');
                setTimeout(() => notification.remove(), 300);
            }
        }, 4000);
    }

    /**
     * Détruit la page
     */
    destroy() {
        this._container = null;
    }
}
